import React, { useState, useEffect, useCallback } from 'react';
import styles from '../../../../styles/LmsForm.module.css'
import closeIcon from '../../../../assets/close.png';
import { notification, Select, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { db, storage } from '../../../../firebase-config';
import { collection, addDoc, updateDoc, doc, getDocs } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import ButtonComponent from '../../../CssComponents/ButtonComponent/ButtonComponent';
import MediaPreviewModal from '../../../CssComponents/MediaPreviewModal/MediaPreviewModal';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // Quill styles

function FormCourse({ initialValues, setInitialValues, showForm, onClose }) {
    const [allModules, setAllModules] = useState([]);
    const [selectedModuleType, setSelectedModuleType] = useState('all');
    const [moduleOptions, setModuleOptions] = useState([]);

    const [bannerFile, setBannerFile] = useState(null);
    const [contentFile, setContentFile] = useState(null);

    const [bannerFileList, setBannerFileList] = useState([]);
    const [contentFileList, setContentFileList] = useState([]);

    const [previewFile, setPreviewFile] = useState({ url: '', type: '' });
    const [previewVisible, setPreviewVisible] = useState(false);

    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchModules = async () => {
            const querySnapshot = await getDocs(collection(db, 'lms_module'));
            const modules = querySnapshot.docs.map(doc => ({
                ...doc.data(),
                moduleId: doc.id,
            }));
            setAllModules(modules);
        };
        fetchModules();
    }, []);

    // Filter modules based on selected type
    useEffect(() => {
        const filteredModules = selectedModuleType === 'all'
            ? allModules
            : allModules.filter(module => module.moduleType === selectedModuleType);

        const options = filteredModules.map(module => ({
            label: module.moduleName,
            value: module.moduleId,
        }));
        setModuleOptions(options);
    }, [allModules, selectedModuleType]);

    useEffect(() => {
        // Initialize file lists for existing course
        if (initialValues.courseBanner) {
            setBannerFileList([{
                uid: initialValues.courseId || '-1',
                name: 'existing_banner',
                status: 'done',
                url: initialValues.courseBanner,
            }]);
        }
        if (initialValues.courseContent) {
            setContentFileList([{
                uid: initialValues.courseId || '-2',
                name: 'existing_content',
                status: 'done',
                url: initialValues.courseContent,
            }]);
        }

        // Cleanup
        return () => {
            setBannerFileList([]);
            setContentFileList([]);
        };
    }, [initialValues.courseBanner, initialValues.courseContent, initialValues.courseId]);

    // Updated handlePreview function
    const handlePreview = useCallback(async (file) => {
        const fileUrl = file.url || file.thumbUrl;
        if (fileUrl) {
            const fileType = file.type || (file.name ? file.name.split('.').pop().toLowerCase() : '');
            let previewType = 'image';
            if (fileType.includes('video') || fileUrl.includes('.mp4') || fileUrl.includes('.webm')) {
                previewType = 'video';
            } else if (fileType.includes('pdf') || fileUrl.includes('.pdf')) {
                previewType = 'pdf';
            }
            setPreviewFile({ url: fileUrl, type: previewType });
            setPreviewVisible(true);
        }
    }, []);

    const handleChange = useCallback((e) => {
        const { name, value } = e.target;
        setInitialValues((prev) => ({
            ...prev,
            [name]: value,
        }));
    }, [setInitialValues]);

    const validateFile = (file, type) => {
        const maxSize = 100 * 1024 * 1024; // 100MB
        if (!file) return false;
        if (file.size > maxSize) {
            notification.error({
                message: 'File too large',
                description: 'File must be smaller than 100MB',
            });
            return false;
        }
        return true;
    };

    const uploadToFirebaseStorage = async (file, path) => {
        try {
            if (!validateFile(file, path)) {
                throw new Error('Invalid file');
            }

            const storageRef = ref(storage, `${path}/${Date.now()}_${file.name}`);
            const snapshot = await uploadBytes(storageRef, file);
            const downloadURL = await getDownloadURL(snapshot.ref);

            return downloadURL;
        } catch (error) {
            console.error('Upload error:', error);
            throw new Error('Failed to upload file');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            let bannerUrl = initialValues.courseBanner || '';
            let contentUrl = initialValues.courseContent || '';
            const isUpdate = !!initialValues.courseId;

            console.log('Banner file state:', bannerFile);
            console.log('Content file state:', contentFile);

            if (bannerFile && bannerFile instanceof File) {
                bannerUrl = await uploadToFirebaseStorage(bannerFile, 'lms_course');
            }
            if (contentFile && contentFile instanceof File) {
                contentUrl = await uploadToFirebaseStorage(contentFile, 'lms_course');
            }

            const courseData = {
                ...initialValues,
                courseBanner: bannerUrl,
                courseContent: contentUrl,
                updatedAt: new Date().toISOString(),
            };

            if (isUpdate) {
                await updateDoc(doc(db, 'lms_course', initialValues.courseId), courseData);
                notification.success({
                    message: 'Course Updated',
                    description: 'Course updated successfully.',
                    placement: 'topRight',
                    duration: 3,
                });
            } else {
                const docRef = await addDoc(collection(db, 'lms_course'), courseData);
                await updateDoc(doc(db, 'lms_course', docRef.id), {
                    courseId: docRef.id,
                    createdAt: new Date().toISOString(),
                });
                notification.success({
                    message: 'Course Created',
                    description: 'New course created successfully.',
                    placement: 'topRight',
                    duration: 3,
                });
            }

            onClose();
            setInitialValues({});
            setBannerFile(null);
            setContentFile(null);
            setBannerFileList([]);
            setContentFileList([]);
        } catch (error) {
            console.error('Error saving course:', error);
            notification.error({
                message: 'Error',
                description: error.message || 'Failed to save course. Please try again.',
                placement: 'topRight',
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleBannerChange = ({ fileList }) => {
        setBannerFileList(fileList);
        const file = fileList.length > 0 ? fileList[0].originFileObj : null;
        console.log('Banner file selected:', file);
        setBannerFile(file);
    };


    return (
        <div className={showForm ? `${styles.modal} ${styles.displayBlock}` : `${styles.modal} ${styles.displayNone}`}>
            <div className={styles.modalMain}>
                <button className={styles.closeBtn} onClick={onClose} disabled={isLoading}>
                    <img src={closeIcon} alt="Close" />
                </button>
                <div className={styles.mainContent}>
                    <div className={styles.title}>Course</div>
                    <form onSubmit={handleSubmit} className={styles.form}>
                        <div className={styles.labelInputWrapper}>
                            <div className={styles.labelText}>Course Name</div>
                            <input
                                name="courseName"
                                className={styles.input}
                                value={initialValues.courseName || ''}
                                onChange={handleChange}
                                placeholder="Enter Course Name"
                                required
                                disabled={isLoading}
                            />
                        </div>

                        <div className={styles.labelInputWrapper}>
                            <div className={styles.labelText}>Course Description</div>
                            <ReactQuill
                                theme="snow"
                                value={initialValues.courseDescription || ''}
                                onChange={(value) => handleChange({ target: { name: 'courseDescription', value } })}
                                placeholder="Enter Course Description"
                                readOnly={isLoading}
                            />
                        </div>

                        {/* Course Type */}
                        <div className={styles.labelInputWrapper}>
                            <div className={styles.labelText}>Course Type</div>
                            <select
                                name="courseType"
                                className={styles.input}
                                value={initialValues.courseType || ''}
                                onChange={handleChange}
                                required
                                disabled={isLoading}
                            >
                                <option value="">Select Course Type</option>
                                <option value="live">Live</option>
                                <option value="digital">Digital</option>
                            </select>
                        </div>



                        <div className={styles.labelInputWrapper}>
                            <div className={styles.labelText}>Course Banner (Image)</div>
                            <Upload
                                accept="image/*"
                                listType="picture-card"
                                beforeUpload={() => false}
                                fileList={bannerFileList}
                                onChange={handleBannerChange}
                                onPreview={handlePreview}
                                showUploadList={{ showRemoveIcon: true }}
                                maxCount={1}
                                disabled={isLoading}
                            >
                                {bannerFileList.length < 1 && (
                                    <div>
                                        <UploadOutlined />
                                        <div style={{ marginTop: 8 }}>Upload <br /> Banner</div>
                                    </div>
                                )}
                            </Upload>
                        </div>


                        <div className={styles.labelInputWrapper}>
                            <div className={styles.labelText}>Module Type Filter</div>
                            <Select
                                value={selectedModuleType}
                                onChange={setSelectedModuleType}
                                placeholder="Select module type"
                                style={{ marginBottom: '10px' }}
                                options={[
                                    { label: 'All Modules', value: 'all' },
                                    { label: 'Digital Modules', value: 'digital' },
                                    { label: 'Live Modules', value: 'live' },
                                ]}
                            />
                        </div>

                        <div className={styles.labelInputWrapper}>
                            <div className={styles.labelText}>Select Modules</div>
                            <Select
                                mode="multiple"
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                placeholder="Select modules"
                                value={initialValues.courseContainingModules || []}
                                onChange={(value) =>
                                    setInitialValues(prev => ({
                                        ...prev,
                                        courseContainingModules: value,
                                    }))
                                }
                                options={moduleOptions}
                            />
                        </div>

                        <ButtonComponent
                            buttonText={initialValues.courseId ? 'Update Course' : 'Save Course'}
                            buttonType="submit"
                            disabled={isLoading}
                            loading={isLoading}
                        />
                    </form>
                </div>
            </div>

            <MediaPreviewModal
                previewVisible={previewVisible}
                setPreviewVisible={setPreviewVisible}
                previewFile={previewFile}
            />
        </div>
    );
}

export default FormCourse;